import { Link } from 'react-router-dom';
import type { Video } from '@/mocks/videos';

interface VideoCardProps {
  video: Video;
}

const categoryNames: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
};

export function VideoCard({ video }: VideoCardProps) {
  return (
    <article className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer">
      <Link to={`/category/${video.category}`} className="block">
        <div className="relative aspect-video overflow-hidden">
          <img
            src={video.image}
            alt={`${video.title} - 番茄视频高清在线观看`}
            title={`${video.title} - 高清${categoryNames[video.category] || ''}免费在线观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 bg-black/70 backdrop-blur-sm text-white text-xs font-medium rounded-md whitespace-nowrap">
              {video.quality}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur-sm text-accent-400 text-xs font-medium rounded-md whitespace-nowrap">
              <i className="ri-star-fill text-xs"></i>
              {video.rating}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <span className="text-white/80 text-xs whitespace-nowrap">{video.duration}</span>
          </div>
        </div>
      </Link>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-1 font-heading">
          <Link to={`/category/${video.category}`} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">
            {video.title}
          </Link>
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
          {video.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400 whitespace-nowrap">{video.year}</span>
          <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
            {categoryNames[video.category] || video.category}
          </span>
        </div>
      </div>
    </article>
  );
}

interface VideoGridProps {
  videos: Video[];
  title: string;
  subtitle?: string;
}

export default function VideoGrid({ videos, title, subtitle }: VideoGridProps) {
  return (
    <section className="py-14 bg-background-50">
      <div className="container-custom">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-foreground-500">{subtitle}</p>
            )}
          </div>
          <Link
            to="/category/dianying"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
          {videos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/category/dianying"
            className="inline-flex items-center gap-1 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}